import { useState, useEffect, useRef, useCallback } from 'react';
import { useAuth } from './useAuth';
import { supabase } from '@/lib/supabaseClient';

interface UseCreditsReturn {
  credits: number | null;
  isLoading: boolean;
  error: string | null;
  refreshCredits: () => Promise<void>;
}

export function useCredits(): UseCreditsReturn {
  const { isAuthenticated } = useAuth();
  const [credits, setCredits] = useState<number | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  
  // Thêm ref để theo dõi thời gian gọi API gần nhất và trạng thái đang gọi
  const lastFetchTimeRef = useRef<number>(0);
  const isFetchingRef = useRef<boolean>(false);
  const THROTTLE_INTERVAL = 5000; // 5 giây giữa các lần gọi API

  const fetchCredits = useCallback(async (force = false) => {
    // Kiểm tra xem đã đăng nhập chưa
    if (!isAuthenticated) {
      setCredits(null);
      return;
    }

    // Kiểm tra cooldown giữa các lần gọi API
    const now = Date.now();
    if (!force && now - lastFetchTimeRef.current < THROTTLE_INTERVAL) {
      console.log('Credits API throttled: Đợi ít nhất 5 giây giữa các lần gọi');
      return;
    }

    // Kiểm tra xem có đang gọi API không
    if (isFetchingRef.current) {
      console.log('Đang có request lấy credits, không gọi lại');
      return;
    }

    setIsLoading(true);
    setError(null);
    isFetchingRef.current = true;
    lastFetchTimeRef.current = now;

    try {
      // Lấy session hiện tại từ Supabase
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) {
        throw new Error('Không tìm thấy session');
      }

      const response = await fetch('/api/credits', {
        headers: {
          'Authorization': `Bearer ${session.access_token}`
        }
      });
      const data = await response.json();

      if (data.success) {
        setCredits(data.data.credits);
      } else {
        setError(data.message || 'Không thể lấy thông tin credits');
      }
    } catch (error) {
      console.error('Error fetching credits:', error);
      setError('Lỗi khi lấy thông tin credits');
    } finally {
      setIsLoading(false);
      isFetchingRef.current = false;
    }
  }, [isAuthenticated]);

  useEffect(() => {
    // Chỉ gọi API khi mới đăng nhập và chưa có dữ liệu
    if (isAuthenticated && credits === null) {
      fetchCredits(true);
    }
  }, [isAuthenticated, fetchCredits, credits]);

  return {
    credits,
    isLoading,
    error,
    refreshCredits: fetchCredits
  };
} 